// RBAC permissions engine and callables (PRD §2 / WP-15).
// Handles custom roles, member role assignment, and granular permission checking.

import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { stripUndefined } from './callable';
import { useAuth } from './useAuth';

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

export const FEATURES = [
  'connect',
  'subscribers',
  'bots',
  'templates',
  'broadcasts',
  'inbox',
  'inbox_number_mask',
  'inbox_advanced',
  'catalog',
  'ads',
  'integrations',
  'google',
  'api',
  'team',
  'billing',
  'settings',
  'ai',
] as const;

export type FeatureKey = (typeof FEATURES)[number];

export const ACTIONS = ['create', 'update', 'delete', 'special'] as const;
export type ActionKey = (typeof ACTIONS)[number];

export interface ActionPermissions {
  create: boolean;
  update: boolean;
  delete: boolean;
  special: boolean;
}

export interface RoleDoc {
  id: string;
  name: string;
  builtIn: boolean;
  permissions: Record<FeatureKey, ActionPermissions>;
  createdAt: number;
  updatedAt: number;
}

export interface MemberDoc {
  uid: string;
  email: string;
  displayName: string | null;
  roleId: string;
  role: 'owner' | 'admin' | 'agent';
  status: string;
  lastLoginAt: number | null;
  lastLoginIp: string | null;
  phoneVerified: boolean;
  subscribersAssigned: number;
  createdAt: number | null;
  updatedAt: number | null;
}

// ── Callables ────────────────────────────────────────────────────────────────
export const roleList = call<Record<string, never>, { roles: RoleDoc[] }>('roleList');

export const roleSave = call<
  { id?: string; name: string; permissions: Record<string, ActionPermissions> },
  { ok: boolean; role: RoleDoc }
>('roleSave');

export const roleDelete = call<{ id: string }, { ok: boolean; reassignedCount: number }>('roleDelete');

export const roleSeed = call<Record<string, never>, { ok: boolean }>('roleSeed');

export const memberList = call<Record<string, never>, { members: MemberDoc[] }>('memberList');

export const memberSetRole = call<
  { uid: string; roleId: string },
  { ok: boolean; roleId: string; role: string }
>('memberSetRole');

export const memberTouchLogin = call<{ ip?: string }, { ok: boolean }>('memberTouchLogin');

// ── Helpers ──────────────────────────────────────────────────────────────────
export function emptyPermissions(): Record<FeatureKey, ActionPermissions> {
  const perms = {} as Record<FeatureKey, ActionPermissions>;
  for (const f of FEATURES) {
    perms[f] = { create: false, update: false, delete: false, special: false };
  }
  return perms;
}

export function fullPermissions(): Record<FeatureKey, ActionPermissions> {
  const perms = {} as Record<FeatureKey, ActionPermissions>;
  for (const f of FEATURES) {
    perms[f] = { create: true, update: true, delete: true, special: true };
  }
  return perms;
}

export function hasPermission(
  role: RoleDoc | null | undefined,
  feature: FeatureKey,
  action: ActionKey = 'create',
): boolean {
  if (!role) return false;
  if (role.id === 'owner') return true;
  return Boolean(role.permissions?.[feature]?.[action]);
}

// ── Permissions Context & Hook ───────────────────────────────────────────────
export interface PermissionsContextValue {
  roles: RoleDoc[];
  currentRole: RoleDoc | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  hasPermission: (feature: FeatureKey, action?: ActionKey) => boolean;
  refreshRoles: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { role: userRoleString } = useAuth();
  const [roles, setRoles] = useState<RoleDoc[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await roleList({});
      if (res.roles) {
        setRoles(res.roles);
      }
    } catch {
      // Ignore initial load failures gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRoles();
  }, [fetchRoles]);

  const currentRole = roles.find((r) => r.id === userRoleString) ||
    roles.find((r) => r.id === 'owner') ||
    null;

  const isOwner = userRoleString === 'owner';
  const isAdmin = userRoleString === 'admin' || isOwner;

  const checkPermission = useCallback(
    (feature: FeatureKey, action: ActionKey = 'create') => {
      if (isOwner) return true;
      if (!currentRole) return false;
      return hasPermission(currentRole, feature, action);
    },
    [isOwner, currentRole],
  );

  return (
    <PermissionsContext.Provider
      value={{
        roles,
        currentRole,
        loading,
        isOwner,
        isAdmin,
        hasPermission: checkPermission,
        refreshRoles: fetchRoles,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Fallback if not wrapped in provider
    return {
      roles: [],
      currentRole: null,
      loading: false,
      isOwner: true,
      isAdmin: true,
      hasPermission: () => true,
      refreshRoles: async () => {},
    };
  }
  return ctx;
}
